import { useState } from 'react';
import axios from 'axios';
import SERVER from '../../hooks/useServer';
import { FaEdit, FaTrash, FaPlus, FaSearch } from 'react-icons/fa';
import '../../styles/pages/Admin/Dashboard.scss';

const AdminShipping = ({ activeTab, shippingFees, setShippingFees }) => {
    const [loading, setLoading] = useState(null);
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(5);
    const [isEditing, setIsEditing] = useState(false);
    const [currentShipping, setCurrentShipping] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');

    // Form states
    const [formData, setFormData] = useState({
        country_code: '',
        country_name: '',
        standard_fee: '',
        express_fee: '',
    });

    // Filter and search functions
    const filteredShipping = shippingFees.filter(shipping => 
        shipping.country_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        shipping.country_code.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Paginated data
    const currentItems = filteredShipping.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleEdit = (shipping) => {
        setIsEditing(true);
        setCurrentShipping(shipping);
        setFormData({
            country_code: shipping.country_code,
            country_name: shipping.country_name,
            standard_fee: shipping.standard_fee,
            express_fee: shipping.express_fee,
        });
    };

    const handleCancel = () => {
        setIsEditing(false);
        setCurrentShipping(null);
        setFormData({
            country_code: '',
            country_name: '',
            standard_fee: '',
            express_fee: '',
        });
    };

    const handleSubmit = async () => {
        try {
            setLoading(true);
            let response;

            if (isEditing && currentShipping) {
                response = await axios.put(
                    `${SERVER}/shipping/${currentShipping.id}`,
                    formData,
                    {
                        headers: {
                            Authorization: `Bearer ${localStorage.getItem('token')}`,
                        },
                    }
                );
                setShippingFees(shippingFees.map(item => 
                    item.id === currentShipping.id ? response.data : item
                ));
            } else {
                response = await axios.post(
                    `${SERVER}/shipping`,
                    formData,
                    {
                        headers: {
                            Authorization: `Bearer ${localStorage.getItem('token')}`,
                        },
                    }
                );
                setShippingFees([...shippingFees, response.data]);
            }

            setError(null);
            handleCancel();
        } catch (err) {
            setError(err.response?.data?.error || err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm("Êtes-vous sûr de vouloir supprimer ces frais de livraison ?")) {
            try {
                await axios.delete(`${SERVER}/shipping/${id}`, {
                    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
                });
                setShippingFees(shippingFees.filter(item => item.id !== id));
            } catch (err) {
                setError(err.response?.data?.error || err.message);
            }
        }
    };

    if (loading) return <div className="loading">Chargement...</div>;
    if (error) return <div className="error">Erreur: {error}</div>;

    return (
        <div className='admin'>
            {/* Search Bar */}
            <div className="search-filter-container">
                <FaSearch className="search-icon" />
                <input
                    type="text"
                    placeholder="Rechercher..."
                    value={searchTerm}
                    onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setCurrentPage(1);
                    }}
                    className="search-input"
                />
                <button 
                    className="add-button"
                    onClick={() => setIsEditing(!isEditing)}
                >
                    <FaPlus /> Ajouter
                </button>
            </div>

            {/* Shipping Tab */}
            {activeTab === 'shipping' && (
                <div className="admin-section">
                    <h2>Frais de Livraison</h2>
                    
                    {/* Shipping Form */}
                    {(isEditing || !currentShipping) && (
                        <div className="admin-form">
                            <div className="form-group">
                                <label>Code Pays (2 lettres)</label>
                                <input
                                    type="text"
                                    name="country_code"
                                    placeholder="ex: FR pour France"
                                    value={formData.country_code}
                                    onChange={handleInputChange}
                                    maxLength="2"
                                    style={{ textTransform: 'uppercase' }}
                                />
                            </div>
                            <div className="form-group">
                                <label>Nom du Pays</label>
                                <input
                                    type="text"
                                    name="country_name"
                                    placeholder="ex: France"
                                    value={formData.country_name}
                                    onChange={handleInputChange}
                                />
                            </div>
                            <div className="form-group">
                                <label>Frais Standard (€)</label>
                                <input
                                    type="number"
                                    name="standard_fee"
                                    placeholder="ex: 21.40"
                                    value={formData.standard_fee}
                                    onChange={handleInputChange}
                                    step="0.01"
                                    min="0"
                                />
                            </div>
                            <div className="form-group">
                                <label>Frais Express (€)</label>
                                <input
                                    type="number"
                                    name="express_fee"
                                    placeholder="ex: 123.00"
                                    value={formData.express_fee}
                                    onChange={handleInputChange}
                                    step="0.01"
                                    min="0"
                                />
                            </div>
                            <div className="form-actions">
                                <button onClick={handleSubmit}>
                                    {currentShipping ? 'Mettre à jour' : 'Ajouter'}
                                </button>
                                <button className="cancel" onClick={handleCancel}>
                                    Annuler
                                </button>
                            </div>
                        </div>
                    )}
                    
                    {/* Shipping Table */}
                    <div className="admin-table-container">
                        <table className="admin-table">
                            <thead>
                                <tr>
                                    <th>Code Pays</th>
                                    <th>Nom du Pays</th>
                                    <th>Standard (€)</th>
                                    <th>Express (€)</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {currentItems.map((shipping) => (
                                    <tr key={shipping.id}>
                                        <td>{shipping.country_code}</td>
                                        <td>{shipping.country_name}</td>
                                        <td>{shipping.standard_fee}</td>
                                        <td>{shipping.express_fee}</td>
                                        <td>
                                            <div className="admin-actions">
                                                <button onClick={() => handleEdit(shipping)}>
                                                    <FaEdit /> Modifier
                                                </button>
                                                <button onClick={() => handleDelete(shipping.id)}>
                                                    <FaTrash /> Supprimer
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        
                        {/* Pagination */}
                        <div className="pagination">
                            {Array.from({ length: Math.ceil(filteredShipping.length / itemsPerPage) }).map((_, index) => (
                                <button
                                    key={index}
                                    onClick={() => setCurrentPage(index + 1)}
                                    className={currentPage === index + 1 ? 'active' : ''}
                                >
                                    {index + 1}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminShipping;